///
/// Copyright (c) 2017-present, Siklu Ltd.
/// All rights reserved.
///

import { Injectable, ViewContainerRef } from '@angular/core';
import { Router } from '@angular/router';
import { AbstractInteractiveWindow } from './abstract-interactive-window';
import { GtthProjectUserSessionService } from '../logical-topology/engine/project-user-session.service';
import { ProjectWindowsEvents } from './project-windows-events.model';
import { GtthEventManager } from '../../services/event-manager.service';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ComponentType } from '@angular/cdk/overlay';

interface ActiveModalComponent {
    modalRef: MatDialogRef<any>;
    component: AbstractInteractiveWindow;
    routerPath: string;
}

@Injectable({
    providedIn: 'root'
})
export class ProjectInteractiveWindowsPopupService {
    public static readonly BOM_REPORT = 'GtthBomReportWindowComponent';
    public static readonly REP_CONNECTED_SITES = 'GtthReportConnectedSitesComponent';
    public static readonly REP_CONSTRAINT_COMPLIANCE = 'GtthReportConstraintComplianceComponent';
    public static readonly REP_CONSTRAINT_COMPLIANCE_NODES_LIST_MAX_HOPS = 'GtthReportConstraintComplianceMaxHopsNodesListComponent';
    public static readonly REP_CONSTRAINT_COMPLIANCE_NODES_LIST_MAX_LINKS = 'GtthReportConstraintComplianceMaxLinksNodesListComponent';
    public static readonly REP_P2MP_INFO = 'GtthReportP2MPInfoComponent';
    public static readonly REP_P2P_INFO = 'GtthReportP2PInfoComponent';
    public static readonly REP_NODES_WITH_INTERFERENCE = 'GtthReportNodesWithInterferenceComponent';
    public static readonly DISABLED_ENTITIES = 'GtthDisabledEntitiesComponent';
    public static readonly CONFIG_RINGS_ERROR = 'GtthConfigRingsErrorReportComponent';
    public static readonly OVERLOADED_SECOTRS_REPORT = 'GtthReportOverloadedSectorsComponent';
    public static readonly MANUAL_DESIGN = 'GtthManualDesignComponent';
    public static readonly TUNNELS_REPORT = 'GtthTunnelsReportWindowComponent';
    public static readonly AN_IP_REPORT = 'GtthAnIpInfoWindowComponent';

    private static readonly MODAL_TOP_OFFSET = 200;
    private static readonly MODAL_BOTTOM_OFFSET = 20;

    private readonly modalRefsMap: Map<string, ActiveModalComponent>;

    private projectViewPanelContainerRef: ViewContainerRef;

    constructor(
        private readonly dialog: MatDialog,
        private readonly router: Router,
        private readonly projectUserSessionService: GtthProjectUserSessionService,
        private readonly eventManager: GtthEventManager
    ) {
        this.modalRefsMap = new Map();

        this.eventManager
            .getEventSubscription(ProjectWindowsEvents.CLOSE_MODAL_WINDOW)
            .subscribe((data) => this.closeAll(data.content?.force));
    }

    setProjectViewPanelContainerRef(viewContainerRef: ViewContainerRef): void {
        this.projectViewPanelContainerRef = viewContainerRef;
    }

    openInteractiveWindow(
        component: ComponentType<any>,
        componentId: string,
        routerPath: string,
        bbox?: number[],
        type?: string
    ): Promise<MatDialogRef<any>> {
        return new Promise<MatDialogRef<any>>((resolve, _reject) => {
            const isOpen = this.modalRefsMap.has(componentId);

            if (isOpen) {
                resolve(this.modalRefsMap.get(componentId).modalRef);
            } else {
                setTimeout(() => {
                    const ngbModalRef = this.projectModalRef(component, componentId, routerPath, bbox, type);
                    resolve(ngbModalRef);
                }, 0);
            }
        });
    }

    public closeWindow(componentId: string, force = false) {
        if (
            this.modalRefsMap &&
            this.modalRefsMap.has(componentId) &&
            (force || this.modalRefsMap.get(componentId).component.isCanBeClosed())
        ) {
            this.modalRefsMap.get(componentId).modalRef.close();
            this.modalRefsMap.delete(componentId);
        }
        window.removeEventListener('resize', this.adjustModalDialogMaxHeight);
    }

    storeActiveWindows() {
        this.projectUserSessionService.clearInteractiveDialogs();
        for (const activeWindow of this.modalRefsMap.values()) {
            this.projectUserSessionService.storeInteractiveDialog(activeWindow.routerPath, activeWindow.component);
        }
    }

    bomReviewIsOpen() {
        return this?.modalRefsMap?.has(ProjectInteractiveWindowsPopupService.BOM_REPORT);
    }

    closeAll(force = false) {
        for (const windowId of this.modalRefsMap.keys()) {
            this.closeWindow(windowId, force);
        }
    }

    nodeCreate(nodeName: string, isEnabled: boolean) {
        for (const window of this.modalRefsMap.values()) {
            window.component.nodeCreate(nodeName, isEnabled);
        }
    }

    nodeDelete(nodeName: string) {
        for (const window of this.modalRefsMap.values()) {
            window.component.nodeDelete(nodeName);
        }
    }

    nodeNameChanged(oldName: string, newName: string) {
        for (const window of this.modalRefsMap.values()) {
            window.component.nodeNameChanged(oldName, newName);
        }
    }

    nodeLayerChanged(name: string, newLayerId: number): void {
        for (const window of this.modalRefsMap.values()) {
            window.component.nodeLayerChanged(name, newLayerId);
        }
    }

    nodeEnabled(name: string, enabled: boolean) {
        for (const window of this.modalRefsMap.values()) {
            window.component.nodeEnabled(name, enabled);
        }
    }

    nodeSelected(name: string, selected: boolean) {
        for (const window of this.modalRefsMap.values()) {
            window.component.nodeSelected(name, selected);
        }
    }

    linkCreate(fromNode: string, toNode: string, isDisabled: boolean) {
        for (const window of this.modalRefsMap.values()) {
            window.component.linkCreate(fromNode, toNode, isDisabled);
        }
    }

    linkDelete(fromNode: string, toNode: string) {
        for (const window of this.modalRefsMap.values()) {
            window.component.linkDelete(fromNode, toNode);
        }
    }

    linkDisabled(fromNode: string, toNode: string, value: boolean) {
        for (const window of this.modalRefsMap.values()) {
            window.component.linkDisabled(fromNode, toNode, value);
        }
    }

    linkFrozen(fromName: string, toName: string, isForced: boolean) {
        for (const window of this.modalRefsMap.values()) {
            window.component.linkFrozen(fromName, toName, isForced);
        }
    }

    linkInstalled(fromName: string, toName: string, isInstalled: boolean) {
        for (const window of this.modalRefsMap.values()) {
            window.component.linkInstalled(fromName, toName, isInstalled);
        }
    }

    linkSelected(fromName: string, toName: string, selected: boolean) {
        for (const window of this.modalRefsMap.values()) {
            window.component.linkSelected(fromName, toName, selected);
        }
    }

    sectorDirection(aggregatedSectorIndex: number, angle: number) {
        for (const window of this.modalRefsMap.values()) {
            window.component.sectorDirection(aggregatedSectorIndex, angle);
        }
    }

    sectorSelected(aggregatedSectorIndex: number, selected: boolean) {
        for (const window of this.modalRefsMap.values()) {
            window.component.sectorSelected(aggregatedSectorIndex, selected);
        }
    }

    private projectModalRef(
        component: ComponentType<any>,
        componentId: string,
        routerPath: string,
        bbox: number[],
        type: string
    ): MatDialogRef<any> {
        const maxHeight =
            window.innerHeight -
            ProjectInteractiveWindowsPopupService.MODAL_TOP_OFFSET -
            ProjectInteractiveWindowsPopupService.MODAL_BOTTOM_OFFSET;
        let height;
        let width = 550;
        let top = ProjectInteractiveWindowsPopupService.MODAL_TOP_OFFSET;
        let left = 10;

        if (bbox) {
            height = window.innerHeight * bbox[0];
            width = window.innerWidth * bbox[1];
            const topBbox = bbox[3] < 0 ? 0 : bbox[3]; // prevent the modal from being positioned above the top screen border
            top = window.innerHeight * topBbox;
            left = window.innerWidth * bbox[2];
        }

        const dialogRef = this.dialog.open(component, {
            // project view panel ViewContainerRef will add project panel Injector to all interactive dialogs
            viewContainerRef: this.projectViewPanelContainerRef,
            width: width + 'px',
            height: (height && height + 'px') || 'auto',
            position: {
                top: top + 'px',
                left: left + 'px'
            },
            minHeight: 200,
            maxHeight: maxHeight + 'px',
            minWidth: 200,
            maxWidth: undefined,
            hasBackdrop: false,
            panelClass: 'draggable-modal-dialog',
            closeOnNavigation: true
        });

        if (
            componentId === ProjectInteractiveWindowsPopupService.REP_CONSTRAINT_COMPLIANCE_NODES_LIST_MAX_HOPS ||
            componentId === ProjectInteractiveWindowsPopupService.REP_CONSTRAINT_COMPLIANCE_NODES_LIST_MAX_LINKS
        ) {
            dialogRef.componentInstance.listType = type;
        } else if (componentId === ProjectInteractiveWindowsPopupService.CONFIG_RINGS_ERROR) {
            dialogRef.componentInstance.modalTitle = type;
        }

        this.modalRefsMap.set(componentId, {
            modalRef: dialogRef,
            component: dialogRef.componentInstance,
            routerPath
        });

        if (dialogRef.componentInstance instanceof AbstractInteractiveWindow) {
            dialogRef.componentInstance.setOffsets(top, left);
        }

        dialogRef.afterClosed().subscribe(() => {
            this.router.navigate([{ outlets: { popup: null } }], { replaceUrl: true });
            this.closeWindow(componentId);
        });

        window.addEventListener('resize', this.adjustModalDialogMaxHeight);

        return dialogRef;
    }

    private adjustModalDialogMaxHeight(): void {
        const dialog = document.querySelector('.draggable-modal-dialog');
        if (dialog) {
            const maxHeight =
                window.innerHeight -
                ProjectInteractiveWindowsPopupService.MODAL_TOP_OFFSET -
                ProjectInteractiveWindowsPopupService.MODAL_BOTTOM_OFFSET;
            const dialogStyle = dialog.getAttribute('style');
            const cleanStyle = dialogStyle.replace(new RegExp(/\s?max-height:\s\d+px;\s?/), '');
            const newDialogStyle = cleanStyle + ` max-height: ${maxHeight}px;`;
            dialog.setAttribute('style', newDialogStyle);
        }
    }
}
